import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { selectUserPreferences, refreshCriticalResource } from '../../../redux/slices/criticalDataSlice';
import PropTypes from 'prop-types';
import { Form, InputGroup, Button, Accordion, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { selectApiUrls } from '../../../redux/slices/apiUrlSlice';
import { selectUserDetails } from '../../../redux/slices/userSlice';
import { postData, deleteData } from '../../../redux/slices/apiCallSlice';
import { canCreateJobs } from '../utils/schedulePermissions';
import { JOB_TYPE_OPTIONS, getJobTypeFromJobNumber } from '../utils/jobTypeUtils';

const normalizeMultiSelectFilter = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (!value || value === 'all') {
    return [];
  }

  return [value];
};

const expandLegacyStationTypeValues = (values) => {
  if (!Array.isArray(values) || values.length === 0) {
    return [];
  }

  if (!values.includes('sky-harvester')) {
    return values;
  }

  return [
    ...values.filter((value) => value !== 'sky-harvester'),
    'sky-harvester-micro',
    'sky-harvester-sh80',
    'sky-harvester-custom'
  ];
};

const defaultFilters = {
  departments: {
    engineering: true,
    fabrication: true,
    controlPanel: true,
    pumps: true,
    assembly: true,
    test: true,
    northPrairie: true,
    shipping: true,
    portalDetails: true
  },
  dueDatesOnly: false,
  softwareView: false,
  controlPanelView: false,
  northPrairieView: false,
  internationalShippingOnly: false,
  additionalFilters: {
    projectManager: 'all',
    salesRep: 'all',
    engineer: 'all',
    stationType: [],
    jobType: [],
    tbdOnly: false,
    moveRequestOnly: false,
    witnessTestOnly: false,
    longBuildOnly: false,
    openChangeOrderTasksOnly: false,
    depositOnly: false
  }
};

const normalizeFilterConfig = (filters) => ({
  ...defaultFilters,
  ...(filters || {}),
  departments: {
    ...defaultFilters.departments,
    ...(filters?.departments || {})
  },
  additionalFilters: {
    ...defaultFilters.additionalFilters,
    ...(filters?.additionalFilters || {})
  }
});

const JobScheduleControls = ({
  handleExportPrintClick,  // Single handler for export/print
  handleOpenMOSearchModal,
  handleOpenNewJobModal,
  userRoles, // Add userRoles prop
  filters,
  handleFilterToggle,
  setFilters,
  weeksToShow,
  setWeeksToShow,
  dataToProcess = [],
  getStationType,
  showSaveNotificationMessage // Add this prop for notifications
}) => {
  // Redux state
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);
  const userDetails = useSelector(selectUserDetails);
  const currentUser = userDetails?.localAccountId;
  const userPreferencesUrl = apiUrls.userPreferences;
  
  // Local state
  const [accordionOpen, setAccordionOpen] = useState(false);
  const [compactControlsOpen, setCompactControlsOpen] = useState(false);
  
  // Saved views state
  const preferences = useSelector(selectUserPreferences);
  const savedViews = useMemo(() => (preferences || []).filter(row =>
    (row.Category ?? row.category) === 'JobScheduleViews' && (row.UserObjectID ?? row.userObjectID) === currentUser),
  [preferences, currentUser]);
  const defaultViewUser = useRef(null);
  const [showSaveViewModal, setShowSaveViewModal] = useState(false);
  const [showLoadViewModal, setShowLoadViewModal] = useState(false);
  const [newViewName, setNewViewName] = useState("");
  const [newViewDescription, setNewViewDescription] = useState("");
  const [isLoadingView, setIsLoadingView] = useState(false);
  const selectedStationTypes = useMemo(() => expandLegacyStationTypeValues(
    normalizeMultiSelectFilter(filters.additionalFilters?.stationType)
  ), [filters.additionalFilters?.stationType]);
  const selectedJobTypes = useMemo(() => normalizeMultiSelectFilter(filters.additionalFilters?.jobType), [filters.additionalFilters?.jobType]);
  const selectedEngineer = filters.additionalFilters?.engineer || 'all';

  const stationTypeOptions = [
    { value: 'large-station', label: 'Large Station' },
    { value: 'sky-harvester-micro', label: 'Sky Harvester Micro' },
    { value: 'sky-harvester-sh80', label: 'Sky Harvester SH80' },
    { value: 'sky-harvester-custom', label: 'Sky Harvester Custom' },
    { value: 'retrofit-kit', label: 'Retrofit Kit' },
    { value: 'watermax-3000', label: 'WaterMax 3000' },
    { value: 'watermax-5000', label: 'WaterMax 5000' },
    { value: 'watermax-7000', label: 'WaterMax 7000' },
    { value: 'watermax-9000', label: 'WaterMax 9000' },
    { value: 'pulse-custom', label: 'Pulse Custom' },
    { value: 'pulse-stock', label: 'Pulse Stock' },
    { value: 'other', label: 'Other' }
  ];

  const handleMultiSelectFilterToggle = (filterType, optionValue, isChecked) => {
    const currentValues = filterType === 'stationType'
      ? selectedStationTypes
      : normalizeMultiSelectFilter(filters.additionalFilters?.[filterType]);
    const nextValues = isChecked
      ? [...new Set([...currentValues, optionValue])]
      : currentValues.filter((value) => value !== optionValue);

    handleAdditionalFilterChange(filterType, nextValues);
  };
    // Handle additional filter changes (including notice filters)
  const handleAdditionalFilterChange = (filterType, value) => {
    // Don't update filters if we're currently loading a view
    if (isLoadingView) return;
    
    setFilters(prev => ({
      ...prev,
      additionalFilters: {
        ...prev.additionalFilters,
        [filterType]: value
      }
    }));
  };

  
  // Check if user can create jobs (Admin/Scheduling only)
  const canCreateNewJobs = canCreateJobs(userRoles || {});
  
  // Apply filters for dropdown data
  const filteredDataForDropdowns = useMemo(() => {
    let filtered = dataToProcess;
    
    // Apply station type filter
    if (selectedStationTypes.length > 0) {
      filtered = filtered.filter(job => 
        selectedStationTypes.includes(getStationType(job))
      );
    }
    
    // Apply job type filter based on JobNumber
    if (selectedJobTypes.length > 0) {
      filtered = filtered.filter(job => {
        const jobType = getJobTypeFromJobNumber(job.JobNumber);
        if (!jobType) return false;
        return selectedJobTypes.includes(jobType);
      });
    }
    
    return filtered;
  }, [dataToProcess, selectedStationTypes, selectedJobTypes, getStationType]);
  
  // Extract unique values from filtered data
  const uniqueProjectManagers = useMemo(() => {
    const managers = new Set();
    // Use filtered data that excludes projectManager filter
    const dataForPM = filters.additionalFilters.salesRep !== 'all' 
      ? filteredDataForDropdowns.filter(job => job.SalesRep === filters.additionalFilters.salesRep)
      : filteredDataForDropdowns;
      
    dataForPM.forEach(job => {
      if (job.ProjectManager) managers.add(job.ProjectManager);
    });
    return Array.from(managers).sort();
  }, [filteredDataForDropdowns, filters.additionalFilters.salesRep]);
  
  const uniqueSalesReps = useMemo(() => {
    const reps = new Set();
    // Use filtered data that excludes salesRep filter
    const dataForSR = filters.additionalFilters.projectManager !== 'all'
      ? filteredDataForDropdowns.filter(job => job.ProjectManager === filters.additionalFilters.projectManager)
      : filteredDataForDropdowns;
      
    dataForSR.forEach(job => {
      if (job.SalesRep) reps.add(job.SalesRep);
    });
    return Array.from(reps).sort();
  }, [filteredDataForDropdowns, filters.additionalFilters.projectManager]);

  const uniqueEngineers = useMemo(() => {
    const engineers = new Set();

    filteredDataForDropdowns.forEach(job => {
      if (job.MechanicalEngineer) engineers.add(job.MechanicalEngineer);
      if (job.ElectricalEngineer) engineers.add(job.ElectricalEngineer);
      if (job.ControlsEngineer) engineers.add(job.ControlsEngineer);
    });

    return Array.from(engineers).sort();
  }, [filteredDataForDropdowns]);
  
  // Helper function to show notifications
  const showNotification = (message, type = "info") => {
    if (showSaveNotificationMessage) {
      showSaveNotificationMessage(message, type);
    }
  };
  
  // Fetch saved views
  const fetchSavedViews = useCallback(async () => {
    if (!userPreferencesUrl || !currentUser) return;
    
    try {
      await dispatch(refreshCriticalResource('userPreferences')).unwrap();
    } catch (err) {
      console.error("Error fetching saved views:", err);
    }
  }, [dispatch, userPreferencesUrl, currentUser]);

  // Save current view configuration
  const saveView = async () => {
    if (!newViewName || !currentUser) return;

    const viewConfig = {
      filters,
      weeksToShow,
      accordionOpen,
      description: newViewDescription
    };

    try {
      await dispatch(postData({
        url: userPreferencesUrl,
        data: {
          userObjectID: currentUser,
          category: "JobScheduleViews",
          key: newViewName,
          value: JSON.stringify(viewConfig)
        }
      })).unwrap();

      setNewViewName("");
      setNewViewDescription("");
      setShowSaveViewModal(false);
      fetchSavedViews();
      showNotification("View saved successfully!", "success");
    } catch (err) {
      console.error("Error saving view:", err);
      showNotification("Error saving view. Please try again.", "error");
    }
  };

  // Load a saved view
  const loadView = (savedView) => {
    try {
      const config = JSON.parse(savedView.Value);
      
      // Set loading flag to prevent unnecessary updates
      setIsLoadingView(true);
      
      setFilters(normalizeFilterConfig(config.filters));
      
      setWeeksToShow(config.weeksToShow || 999);
      setAccordionOpen(config.accordionOpen || false);
      
      // Clear loading flag after a brief delay
      setTimeout(() => setIsLoadingView(false), 100);
      
      setShowLoadViewModal(false);
      showNotification(`View "${savedView.Key}" loaded successfully!`, "success");
    } catch (err) {
      console.error("Error loading view:", err);
      showNotification("Error loading view. Please try again.", "error");
      setIsLoadingView(false);
    }
  };

  // Delete a saved view
  const deleteView = async (viewId) => {
    try {
      await dispatch(deleteData({
        url: userPreferencesUrl,
        id: viewId
      })).unwrap();
      fetchSavedViews();
      showNotification("View deleted successfully!", "success");
    } catch (err) {
      console.error("Error deleting view:", err);
      showNotification("Error deleting view. Please try again.", "error");
    }
  };

  // Try to load default preferences on mount
  useEffect(() => {
    if (!currentUser) { defaultViewUser.current = null; return; }
    if (!Array.isArray(preferences) || defaultViewUser.current === currentUser) return;
    defaultViewUser.current = currentUser;
    const loadDefaultPreferences = async () => {
      if (savedViews.length === 0) return;
      
      // Look for a view named "Default"
      const defaultView = savedViews.find(view => 
        String(view.Key ?? view.key).toLowerCase() === 'default' || String(view.Key ?? view.key).toLowerCase() === 'default view'
      );
      
      if (defaultView) {
        try {
          const config = JSON.parse(defaultView.Value);
          
          // Load the default configuration silently (without notification)
          setFilters(normalizeFilterConfig(config.filters));
          
          setWeeksToShow(config.weeksToShow || 999);
          setAccordionOpen(config.accordionOpen || false);
        } catch (err) {
          console.error("Error loading default preferences:", err);
        }
      }
    };
    
    // Only run after saved views have been loaded
    if (savedViews.length > 0) {
      loadDefaultPreferences();
    }
  }, [currentUser, preferences, savedViews, setFilters, setWeeksToShow]);
  
  return (
    <div className="controls-section">
      <div className="search-and-create-container d-flex justify-content-end align-items-center mb-3">
        <div className="button-group d-flex">
          <Button 
            variant="outline-success" 
            onClick={() => setShowSaveViewModal(true)}
            className="me-2"
            title="Save current filter configuration"
          >
            <i className="bi bi-floppy me-2"></i>
            Save View
          </Button>
          <Button 
            variant="outline-primary" 
            onClick={() => setShowLoadViewModal(true)}
            className="me-2"
            title="Load saved filter configuration"
          >
            <i className="bi bi-folder-open me-2"></i>
            Load View
          </Button>
          <Button 
            variant="primary" 
            onClick={handleExportPrintClick}
            className="export-print-button me-2"
          >
            <i className="bi bi-download me-2"></i>
            Export / Print
          </Button>
          {canCreateNewJobs && (
            <>
              <Button 
                variant="info" 
                onClick={handleOpenMOSearchModal}
                className="add-mo-button me-2"
              >
                <i className="bi bi-plus-circle me-2"></i>
                Add MO
              </Button>
              <Button 
                variant="primary" 
                onClick={handleOpenNewJobModal}
                className="create-job-button"
              >
                <i className="bi bi-plus-circle me-2"></i>
                Create New Job
              </Button>
            </>
          )}
        </div>
      </div>

      <Button
        variant="outline-primary"
        className="schedule-compact-controls-toggle mb-3"
        onClick={() => setCompactControlsOpen(isOpen => !isOpen)}
        aria-expanded={compactControlsOpen}
        aria-controls="schedule-compact-controls"
      >
        <i className="bi bi-sliders me-2"></i>
        Sections, views, and filters
        <i className={`bi ${compactControlsOpen ? 'bi-chevron-up' : 'bi-chevron-down'} ms-auto`}></i>
      </Button>

      <div
        id="schedule-compact-controls"
        className={`schedule-compact-controls-panel ${compactControlsOpen ? 'is-open' : ''}`}
      >
      <div className="schedule-compact-view-actions mb-3">
        <Button
          variant="outline-success"
          onClick={() => setShowSaveViewModal(true)}
          title="Save current filter configuration"
        >
          <i className="bi bi-floppy me-2"></i>
          Save View
        </Button>
        <Button
          variant="outline-primary"
          onClick={() => setShowLoadViewModal(true)}
          title="Load saved filter configuration"
        >
          <i className="bi bi-folder-open me-2"></i>
          Load View
        </Button>
        <Button
          variant="primary"
          onClick={handleExportPrintClick}
        >
          <i className="bi bi-download me-2"></i>
          Export / Print
        </Button>
      </div>
      <div className="filters mb-3">        <div className="d-flex justify-content-between align-items-start">
          <div className="filter-group flex-grow-1">
            <h6 className="filter-group-title">Visible Sections:</h6>
            <div className="d-flex flex-wrap">
              {Object.entries({
                engineering: 'Engineering',
                fabrication: 'Fabrication',
                controlPanel: 'Control Panel',
                pumps: 'Pumps',
                assembly: 'Assembly',
                test: 'Test',
                northPrairie: 'North Prairie',
                shipping: 'Shipping',
                portalDetails: 'Portal Details'
              }).map(([key, label]) => (
                <div key={key} className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`${key}Filter`}
                    checked={filters.departments[key]}
                    onChange={() => handleFilterToggle(key)}
                  />
                  <label className="form-check-label" htmlFor={`${key}Filter`}>
                    {label}
                  </label>
                </div>
              ))}
            </div>

            <div className="view-filter-row mt-3">
              <h6 className="filter-group-title">Views & Filters:</h6>
              <div className="d-flex flex-wrap">
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="dueDatesOnlyFilter"
                    checked={filters.dueDatesOnly}
                    onChange={() => setFilters(prev => ({ ...prev, dueDatesOnly: !prev.dueDatesOnly }))}
                  />
                  <label className="form-check-label" htmlFor="dueDatesOnlyFilter">
                    <strong>Due Dates Only</strong>
                  </label>
                </div>
                
                <div className="form-check form-check-inline ms-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="softwareViewFilter"
                    checked={filters.softwareView}
                    onChange={() => setFilters(prev => ({
                      ...prev,
                      softwareView: !prev.softwareView,
                      controlPanelView: false,
                      northPrairieView: false
                    }))}
                  />
                  <label className="form-check-label" htmlFor="softwareViewFilter">
                    <strong>Software View</strong>
                  </label>
                </div>

                <div className="form-check form-check-inline ms-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="controlPanelViewFilter"
                    checked={Boolean(filters.controlPanelView)}
                    onChange={() => setFilters(prev => ({
                      ...prev,
                      controlPanelView: !prev.controlPanelView,
                      softwareView: false,
                      northPrairieView: false
                    }))}
                  />
                  <label className="form-check-label" htmlFor="controlPanelViewFilter">
                    <strong>Control Panel View</strong>
                  </label>
                </div>

                <div className="form-check form-check-inline ms-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="northPrairieViewFilter"
                    checked={Boolean(filters.northPrairieView)}
                    onChange={() => setFilters(prev => ({
                      ...prev,
                      northPrairieView: !prev.northPrairieView,
                      softwareView: false,
                      controlPanelView: false
                    }))}
                  />
                  <label className="form-check-label" htmlFor="northPrairieViewFilter">
                    <strong>North Prairie View</strong>
                  </label>
                </div>

                <div className="form-check form-check-inline ms-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="internationalShippingOnlyFilter"
                    checked={Boolean(filters.internationalShippingOnly)}
                    onChange={() => setFilters(prev => ({
                      ...prev,
                      internationalShippingOnly: !prev.internationalShippingOnly
                    }))}
                  />
                  <label className="form-check-label" htmlFor="internationalShippingOnlyFilter">
                    <strong>International Shipping</strong>
                  </label>
                </div>
              </div>
            </div>

          </div>
          
          {/* Weeks selector moved here - only show when not in focused views */}
          {!filters.softwareView && !filters.controlPanelView && !filters.northPrairieView && (
            <div className="weeks-selector ms-3">
              <h6 className="filter-group-title">&nbsp;</h6>
              <InputGroup>
                <InputGroup.Text>
                  Weeks to show
                </InputGroup.Text>
                <Form.Select 
                  value={weeksToShow}
                  onChange={(e) => setWeeksToShow(Number(e.target.value))}
                  className="form-control"
                  style={{ width: 'auto' }}
                >
                  <option value="1">1 Week</option>
                  <option value="2">2 Weeks</option>
                  <option value="3">3 Weeks</option>
                  <option value="4">4 Weeks</option>
                  <option value="6">6 Weeks</option>
                  <option value="8">8 Weeks</option>
                  <option value="12">12 Weeks</option>
                  <option value="999">All Weeks</option>
                </Form.Select>
              </InputGroup>
            </div>
          )}
        </div>
        
        {/* New Accordion for Additional Filters */}
        <Accordion className="mt-3">
          <Accordion.Item eventKey="0">            <Accordion.Header onClick={() => setAccordionOpen(!accordionOpen)}>
              Additional Filters
              {(filters.additionalFilters?.projectManager !== 'all' || 
                filters.additionalFilters?.salesRep !== 'all' || 
                selectedEngineer !== 'all' ||
                selectedStationTypes.length > 0 ||
                selectedJobTypes.length > 0 ||
                filters.additionalFilters?.tbdOnly ||
                filters.additionalFilters?.moveRequestOnly ||
                filters.additionalFilters?.witnessTestOnly ||
                filters.additionalFilters?.longBuildOnly ||
                filters.additionalFilters?.openChangeOrderTasksOnly ||
                filters.additionalFilters?.depositOnly) && 
                <span className="text-primary ms-2">(Active)</span>
              }
            </Accordion.Header>
            <Accordion.Body>
              <div className="row">
                <div className="col-md-3">
                  <Form.Group>
                    <Form.Label>Project Manager</Form.Label>
                    <Form.Select
                      value={filters.additionalFilters?.projectManager || 'all'}
                      onChange={(e) => handleAdditionalFilterChange('projectManager', e.target.value)}
                    >
                      <option value="all">All Project Managers</option>
                      {uniqueProjectManagers.map(pm => (
                        <option key={pm} value={pm}>{pm}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                
                <div className="col-md-3">
                  <Form.Group>
                    <Form.Label>Sales Rep</Form.Label>
                    <Form.Select
                      value={filters.additionalFilters?.salesRep || 'all'}
                      onChange={(e) => handleAdditionalFilterChange('salesRep', e.target.value)}
                    >
                      <option value="all">All Sales Reps</option>
                      {uniqueSalesReps.map(rep => (
                        <option key={rep} value={rep}>{rep}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>

                <div className="col-md-3">
                  <Form.Group>
                    <Form.Label>Engineer</Form.Label>
                    <Form.Select
                      value={selectedEngineer}
                      onChange={(e) => handleAdditionalFilterChange('engineer', e.target.value)}
                    >
                      <option value="all">All Engineers</option>
                      {uniqueEngineers.map(engineer => (
                        <option key={engineer} value={engineer}>{engineer}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                
                <div className="col-md-3">
                  <Form.Group>
                    <Form.Label>Station Type</Form.Label>
                    <div className="border rounded p-2" style={{ maxHeight: '165px', overflowY: 'auto' }}>
                      {stationTypeOptions.map((option) => (
                        <Form.Check
                          key={option.value}
                          type="checkbox"
                          id={`stationType-${option.value}`}
                          label={option.label}
                          checked={selectedStationTypes.includes(option.value)}
                          onChange={(e) => handleMultiSelectFilterToggle('stationType', option.value, e.target.checked)}
                          className="mb-1"
                        />
                      ))}
                    </div>
                  </Form.Group>
                </div>
                
                <div className="col-md-3">
                  <Form.Group>
                    <Form.Label>Job Type</Form.Label>
                    <div className="border rounded p-2" style={{ maxHeight: '165px', overflowY: 'auto' }}>
                      {JOB_TYPE_OPTIONS.map((option) => (
                        <Form.Check
                          key={option.value}
                          type="checkbox"
                          id={`jobType-${option.value}`}
                          label={option.label}
                          checked={selectedJobTypes.includes(option.value)}
                          onChange={(e) => handleMultiSelectFilterToggle('jobType', option.value, e.target.checked)}
                          className="mb-1"
                        />
                      ))}
                    </div>
                  </Form.Group>
                </div>
              </div>
              <small className="text-muted d-block mt-1">
                Station Type and Job Type support multi-select. Leave all unchecked to include all values.
              </small>
              
              {/* Notice Filters Section */}
              <div className="row mt-3">
                <div className="col-12">
                  <h6 className="mb-2">Notice Filters:</h6>
                  <div className="d-flex flex-wrap gap-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="tbdOnlyFilter"
                        checked={filters.additionalFilters?.tbdOnly || false}
                        onChange={(e) => handleAdditionalFilterChange('tbdOnly', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="tbdOnlyFilter">
                        <i className="bi bi-exclamation-lg me-1" style={{ color: "red" }}></i>
                        TBDs Only
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="moveRequestOnlyFilter"
                        checked={filters.additionalFilters?.moveRequestOnly || false}
                        onChange={(e) => handleAdditionalFilterChange('moveRequestOnly', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="moveRequestOnlyFilter">
                        <i className="bi bi-arrows-move me-1" style={{ color: "blue" }}></i>
                        Move Requests Only
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="witnessTestOnlyFilter"
                        checked={filters.additionalFilters?.witnessTestOnly || false}
                        onChange={(e) => handleAdditionalFilterChange('witnessTestOnly', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="witnessTestOnlyFilter">
                        <i className="bi bi-search me-1" style={{ color: "black" }}></i>
                        Witness Tests Only
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="longBuildOnlyFilter"
                        checked={filters.additionalFilters?.longBuildOnly || false}
                        onChange={(e) => handleAdditionalFilterChange('longBuildOnly', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="longBuildOnlyFilter">
                        <i className="bi bi-arrows-vertical me-1" style={{ color: "orange" }}></i>
                        Long Builds Only
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="openChangeOrderTasksOnlyFilter"
                        checked={filters.additionalFilters?.openChangeOrderTasksOnly || false}
                        onChange={(e) => handleAdditionalFilterChange('openChangeOrderTasksOnly', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="openChangeOrderTasksOnlyFilter">
                        <i className="bi bi-list-task me-1" style={{ color: "#6f42c1" }}></i>
                        Open Change Order Tasks Only
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="depositOnlyFilter"
                        checked={filters.additionalFilters?.depositOnly || false}
                        onChange={(e) => handleAdditionalFilterChange('depositOnly', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="depositOnlyFilter">
                        <i className="bi bi-cash-coin me-1" style={{ color: "green" }}></i>
                        Payment Terms = DP or PBS
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              
                {/* Clear Filters Button */}
              {(filters.additionalFilters?.projectManager !== 'all' || 
                filters.additionalFilters?.salesRep !== 'all' || 
                selectedEngineer !== 'all' ||
                selectedStationTypes.length > 0 ||
                selectedJobTypes.length > 0 ||
                filters.additionalFilters?.tbdOnly ||
                filters.additionalFilters?.moveRequestOnly ||
                filters.additionalFilters?.witnessTestOnly ||
                filters.additionalFilters?.longBuildOnly ||
                filters.additionalFilters?.openChangeOrderTasksOnly ||
                filters.additionalFilters?.depositOnly) && (
                <Button 
                  variant="link" 
                  className="mt-2 p-0"
                  onClick={() => {
                    setFilters(prev => ({
                      ...prev,
                      additionalFilters: {
                        projectManager: 'all',
                        salesRep: 'all',
                        engineer: 'all',
                        stationType: [],
                        jobType: [],
                        tbdOnly: false,
                        moveRequestOnly: false,
                        witnessTestOnly: false,
                        longBuildOnly: false,
                        openChangeOrderTasksOnly: false,
                        depositOnly: false
                      }
                    }));
                  }}
                >
                  Clear Additional Filters
                </Button>
              )}
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>
      </div>
      
      {/* Save View Modal */}
      <Modal className="job-schedule-modal" show={showSaveViewModal} onHide={() => setShowSaveViewModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Save Filter View</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>View Name</Form.Label>
            <Form.Control
              type="text"
              value={newViewName}
              onChange={(e) => setNewViewName(e.target.value)}
              placeholder="Enter a name for this view"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={newViewDescription}
              onChange={(e) => setNewViewDescription(e.target.value)}
              placeholder="Enter a description for this view"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSaveViewModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveView} disabled={!newViewName}>
            Save View
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Load View Modal */}
      <Modal className="job-schedule-modal" show={showLoadViewModal} onHide={() => setShowLoadViewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Load Saved View</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {savedViews.length === 0 ? (
            <p>No saved views found.</p>
          ) : (
            <div className="saved-views-list">
              {savedViews.map(view => (
                <div key={view.ID} className="d-flex justify-content-between align-items-center p-3 border-bottom">
                  <div>
                    <h6 className="mb-1">{view.Key}</h6>
                    {(() => {
                      try {
                        const config = JSON.parse(view.Value);
                        return config.description && (
                          <small className="text-muted">{config.description}</small>
                        );
                      } catch {
                        return null;
                      }
                    })()}
                  </div>
                  <div>
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      onClick={() => loadView(view)}
                      className="me-2"
                    >
                      Load
                    </Button>
                    <Button 
                      variant="outline-danger" 
                      size="sm" 
                      onClick={() => deleteView(view.ID)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLoadViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

JobScheduleControls.propTypes = {
  handleExportPrintClick: PropTypes.func.isRequired,
  handleOpenMOSearchModal: PropTypes.func.isRequired,
  handleOpenNewJobModal: PropTypes.func.isRequired,
  hasEditAccess: PropTypes.bool.isRequired,
  userRoles: PropTypes.object,
  filters: PropTypes.object.isRequired,
  handleFilterToggle: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  weeksToShow: PropTypes.number.isRequired,
  setWeeksToShow: PropTypes.func.isRequired,
  dataToProcess: PropTypes.array,
  getStationType: PropTypes.func.isRequired,
  showSaveNotificationMessage: PropTypes.func,
};

export default JobScheduleControls;
